import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	type Dispatch,
	type SetStateAction,
} from "react";
import type {
	DockSurfaceState,
	PopupSurfaceState,
} from "@/surfaces/SurfaceTypes";
import type { EngagementProvider } from "@/provider/EngagementProvider";
import type { ProviderRuntimeSnapshot } from "@/provider/ProviderTypes";
import {
	createInitialShellRuntimeState,
	shellRuntimeReducer,
	type ShellRuntimeAction,
	type ShellDockHostMap,
} from "@/shell/ShellRuntimeReducer";
import {
	type ShellPopupPhase,
	isPopupVisiblePhase,
	resolvePopupCloseTargetPhase,
} from "@/shell/ShellRuntimeTypes";
import { debugLog } from "@/utils/debug";
import { useShellPopupController } from "@/shell/useShellPopupController";
import { useShellProviderEvents } from "@/shell/useShellProviderEvents";
import { useShellOrchestrator } from "@/shell/useShellOrchestrator";
import type { ActiveDockTarget } from "@/orchestrators/useCaptainUpIntegration";

export type UseShellRuntimeInput = {
	hostTransportReady: boolean;
	hostConfigured: boolean;
	hostGameReady: boolean;
	providerBootEligible: boolean;

	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;

	activeBootGeneration: number;
	readyBootGeneration: number | null;
	inFlightBootGeneration: number | null;

	requestFreshProviderBoot: () => void;
};

export type UseShellRuntimeState = {
	activeDockTarget: ActiveDockTarget;
	setActiveDockTarget: Dispatch<SetStateAction<ActiveDockTarget>>;

	activeDockHasContent: boolean;

	popupVisible: boolean;
	popupOpenIntentId: number;
	setPopupVisible: Dispatch<SetStateAction<boolean>>;

	dockState: DockSurfaceState | null;
	setDockState: Dispatch<SetStateAction<DockSurfaceState | null>>;

	popupState: PopupSurfaceState | null;
	setPopupState: Dispatch<SetStateAction<PopupSurfaceState | null>>;

	dockHosts: ShellDockHostMap;
	setDockHost: (
		target: ActiveDockTarget
	) => Dispatch<SetStateAction<HTMLDivElement | null>>;

	requestPopupOpen: () => void;
	requestFreshProviderBoot: () => void;
	handlePopupClose: () => void;
};

function resolveNextValue<T>(
	currentValue: T,
	nextValue: SetStateAction<T>
): T {
	return typeof nextValue === "function"
		? (nextValue as (previousValue: T) => T)(currentValue)
		: nextValue;
}

function isSameDockState(
	left: DockSurfaceState | null,
	right: DockSurfaceState | null
): boolean {
	if (left === right) {
		return true;
	}

	if (!left || !right) {
		return false;
	}

	return (
		left.hasArea === right.hasArea &&
		left.visible === right.visible &&
		left.enabled === right.enabled &&
		left.parked === right.parked &&
		left.visibleHost === right.visibleHost &&
		left.parkingHost === right.parkingHost
	);
}

function isSamePopupState(
	left: PopupSurfaceState | null,
	right: PopupSurfaceState | null
): boolean {
	if (left === right) {
		return true;
	}

	if (!left || !right) {
		return false;
	}

	return (
		left.visible === right.visible &&
		left.enabled === right.enabled &&
		left.granted === right.granted &&
		left.parked === right.parked &&
		left.modalHost === right.modalHost &&
		left.parkingHost === right.parkingHost
	);
}

export function useShellRuntime({
	hostTransportReady,
	hostConfigured,
	hostGameReady,
	providerBootEligible,
	provider,
	providerReady,
	providerRuntimeSnapshot,
	activeBootGeneration,
	readyBootGeneration,
	inFlightBootGeneration,
	requestFreshProviderBoot: triggerFreshProviderBoot,
}: UseShellRuntimeInput): UseShellRuntimeState {
	const [state, dispatch] = useReducer(
		shellRuntimeReducer,
		undefined,
		createInitialShellRuntimeState
	);

	const popupVisible = useMemo(
		() => isPopupVisiblePhase(state.popupPhase),
		[state.popupPhase]
	);

	const activeDockHasContent = useMemo(() => {
		return state.activeDockTarget === "mission"
			? state.dockContentState.missionHasContent
			: state.dockContentState.tournamentHasContent;
	}, [
		state.activeDockTarget,
		state.dockContentState.missionHasContent,
		state.dockContentState.tournamentHasContent,
	]);

	const popupGranted = state.popupState?.granted ?? false;

	const popupPhaseRef = useRef<ShellPopupPhase>(state.popupPhase);
	const popupDesiredOpenRef = useRef(state.popupDesiredOpen);
	const popupGrantedRef = useRef(popupGranted);
	const activeDockTargetRef = useRef(state.activeDockTarget);
	const dockStateRef = useRef(state.dockState);
	const popupStateRef = useRef(state.popupState);
	const dockHostsRef = useRef(state.dockHosts);
	const dockHostSetterMapRef = useRef<
		Partial<
			Record<
				ActiveDockTarget,
				Dispatch<SetStateAction<HTMLDivElement | null>>
			>
		>
	>({});

	useEffect(() => {
		popupPhaseRef.current = state.popupPhase;
	}, [state.popupPhase]);

	useEffect(() => {
		popupDesiredOpenRef.current = state.popupDesiredOpen;
	}, [state.popupDesiredOpen]);

	useEffect(() => {
		popupGrantedRef.current = popupGranted;
	}, [popupGranted]);

	useEffect(() => {
		activeDockTargetRef.current = state.activeDockTarget;
	}, [state.activeDockTarget]);

	useEffect(() => {
		dockStateRef.current = state.dockState;
	}, [state.dockState]);

	useEffect(() => {
		popupStateRef.current = state.popupState;
	}, [state.popupState]);

	useEffect(() => {
		dockHostsRef.current = state.dockHosts;
	}, [state.dockHosts]);

	const setActiveDockTarget = useCallback<
		Dispatch<SetStateAction<ActiveDockTarget>>
	>((next) => {
		const currentValue = activeDockTargetRef.current;
		const resolvedValue = resolveNextValue(currentValue, next);

		if (currentValue === resolvedValue) {
			debugLog("[EF ShellRuntime] setActiveDockTarget noop", {
				current: currentValue,
				next: resolvedValue,
			});
			return;
		}

		debugLog("[EF ShellRuntime] setActiveDockTarget", {
			current: currentValue,
			next: resolvedValue,
		});

		dispatch({
			type: "SET_ACTIVE_DOCK_TARGET",
			payload: resolvedValue,
		});
	}, []);

	const setDockState = useCallback<
		Dispatch<SetStateAction<DockSurfaceState | null>>
	>((next) => {
		const currentValue = dockStateRef.current;
		const resolvedValue = resolveNextValue(currentValue, next);

		if (isSameDockState(currentValue, resolvedValue)) {
			debugLog("[EF ShellRuntime] setDockState noop", {
				current: currentValue,
				next: resolvedValue,
			});
			return;
		}

		debugLog("[EF ShellRuntime] setDockState", {
			current: currentValue,
			next: resolvedValue,
		});

		dispatch({
			type: "SET_DOCK_STATE",
			payload: resolvedValue,
		});
	}, []);

	const setPopupState = useCallback<
		Dispatch<SetStateAction<PopupSurfaceState | null>>
	>((next) => {
		const currentValue = popupStateRef.current;
		const resolvedValue = resolveNextValue(currentValue, next);

		if (isSamePopupState(currentValue, resolvedValue)) {
			debugLog("[EF ShellRuntime] setPopupState noop", {
				current: currentValue,
				next: resolvedValue,
			});
			return;
		}

		debugLog("[EF ShellRuntime] setPopupState", {
			current: currentValue,
			next: resolvedValue,
		});

		dispatch({
			type: "SET_POPUP_STATE",
			payload: resolvedValue,
		});
	}, []);

	const setDockHost = useCallback(
		(
			target: ActiveDockTarget
		): Dispatch<SetStateAction<HTMLDivElement | null>> => {
			const existingSetter = dockHostSetterMapRef.current[target];

			if (existingSetter) {
				return existingSetter;
			}

			const stableSetter: Dispatch<SetStateAction<HTMLDivElement | null>> = (
				next
			) => {
				const currentValue = dockHostsRef.current[target] ?? null;
				const resolvedValue = resolveNextValue(currentValue, next);

				if (currentValue === resolvedValue) {
					debugLog("[EF ShellRuntime] setDockHost noop", {
						target,
						currentPresent: !!currentValue,
						nextPresent: !!resolvedValue,
					});
					return;
				}

				debugLog("[EF ShellRuntime] setDockHost", {
					target,
					currentPresent: !!currentValue,
					nextPresent: !!resolvedValue,
				});

				dispatch({
					type: "SET_DOCK_HOST",
					payload: {
						target,
						host: resolvedValue,
					},
				});
			};

			dockHostSetterMapRef.current[target] = stableSetter;

			return stableSetter;
		},
		[]
	);

	const requestPopupOpen = useCallback(() => {
		debugLog("[EF ShellRuntime] popup open requested", {
			popupPhase: popupPhaseRef.current,
			popupDesiredOpen: popupDesiredOpenRef.current,
			popupGranted: popupGrantedRef.current,
		});

		dispatch({ type: "REQUEST_POPUP_OPEN" });
	}, []);

	const requestFreshProviderBoot = useCallback(() => {
		debugLog("[EF ShellRuntime] retry provider boot");

		dispatch({ type: "REQUEST_FRESH_PROVIDER_BOOT" });
		triggerFreshProviderBoot();
	}, [triggerFreshProviderBoot]);

	const handlePopupClose = useCallback(() => {
		debugLog("[EF ShellRuntime] popup close requested");

		if (!popupDesiredOpenRef.current) {
			debugLog(
				"[EF ShellRuntime] popup close request ignored because popup is already desired closed",
				{
					popupPhase: popupPhaseRef.current,
					popupGranted: popupGrantedRef.current,
				}
			);
			return;
		}

		dispatch({
			type: "REQUEST_POPUP_CLOSE",
			payload: {
				nextPhase: resolvePopupCloseTargetPhase(
					popupGrantedRef.current
				),
			},
		});
	}, []);

	useShellProviderEvents({
		provider,
		dispatch,
		activeDockTargetRef,
		popupGrantedRef,
	});

	useShellPopupController({
		hostTransportReady,
		hostConfigured,
		hostGameReady,
		providerBootEligible,
		provider,
		providerReady,
		providerRuntimeSnapshot,
		activeBootGeneration,
		readyBootGeneration,
		inFlightBootGeneration,
		state,
		dispatch,
		triggerFreshProviderBoot,
	});

	useShellOrchestrator({
		state,
		hostTransportReady,
		hostConfigured,
		hostGameReady,
		providerBootEligible,
		provider,
		providerReady,
		providerRuntimeSnapshot,
		activeBootGeneration,
		readyBootGeneration,
		inFlightBootGeneration,
	});

	useEffect(() => {
		debugLog("[EF ShellRuntime] state snapshot", {
			popupPhase: state.popupPhase,
			popupDesiredOpen: state.popupDesiredOpen,
			popupOpenIntentId: state.popupOpenIntentId,
			popupVisible,
			pendingPopupGeneration: state.pendingPopupGeneration,
			freshBootRequiredBeforeNextOpen:
				state.freshBootRequiredBeforeNextOpen,
			activeDockTarget: state.activeDockTarget,
			activeDockHasContent,
			hasPopupState: !!state.popupState,
			hasDockState: !!state.dockState,
			dockHosts: {
				mission: !!state.dockHosts.mission,
				tournament: !!state.dockHosts.tournament,
			},
		});
	}, [state, popupVisible, activeDockHasContent]);

	return {
		activeDockTarget: state.activeDockTarget,
		setActiveDockTarget,

		activeDockHasContent,

		popupVisible,
		popupOpenIntentId: state.popupOpenIntentId,
		setPopupVisible: (next) => {
			const resolvedValue = resolveNextValue(
				popupDesiredOpenRef.current,
				next
			);

			if (resolvedValue === popupDesiredOpenRef.current) {
				debugLog("[EF ShellRuntime] setPopupVisible noop", {
					currentDesiredOpen: popupDesiredOpenRef.current,
					nextDesiredOpen: resolvedValue,
					popupPhase: popupPhaseRef.current,
					popupGranted: popupGrantedRef.current,
				});
				return;
			}

			if (resolvedValue) {
				dispatch({ type: "REQUEST_POPUP_OPEN" });
				return;
			}

			dispatch({
				type: "REQUEST_POPUP_CLOSE",
				payload: {
					nextPhase: resolvePopupCloseTargetPhase(
						popupGrantedRef.current
					),
				},
			});
		},

		dockState: state.dockState,
		setDockState,

		popupState: state.popupState,
		setPopupState,

		dockHosts: state.dockHosts,
		setDockHost,

		requestPopupOpen,
		requestFreshProviderBoot,
		handlePopupClose,
	};
}

export default useShellRuntime;